/**
 * Parsing Workflow Modal Component
 * 
 * Step-by-step post-parsing workflow:
 * 1. Parsing Results
 * 2. AI Analysis (with config from CRM)
 * 3. Manual Editing (remove/edit domains)
 * 4. Upload Preview
 * 5. Confirm & Upload to VDL
 * 6. Upload Results (success/failure list)
 */

import { useState, useEffect } from 'react'
import {
  Modal,
  Steps,
  Button,
  Space,
  Alert,
  Table,
  Card,
  Typography,
  message,
  Spin,
  Tag,
  Badge,
  Progress,
  Checkbox,
  Popconfirm,
  Input,
  Tooltip,
  Divider,
  Result,
  Statistic,
  Row,
  Col,
  Segmented,
  Empty,
  Popover,
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  RocketOutlined,
  DeleteOutlined,
  EditOutlined,
  CloudUploadOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  WarningOutlined,
  LoadingOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  SaveOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import api from '@/services/api'

const { Text, Title } = Typography
const { Step } = Steps
const { Search } = Input

// * Domain with analysis result
interface DomainWithAnalysis {
  host: string
  full_url?: string  // Full URL with path
  domain?: string    // Domain without www.
  path?: string | null  // URL path (e.g., /ru/coding)
  total_clicks: number
  queries_count: number
  average_position: number
  popularity: string
  selected: boolean // For upload
  // Phone extraction
  phone_extraction_status?: string  // not_started | extracting | completed | failed
  phone_extraction_method?: string
  phone_extraction_error?: string
  phones_extracted_count?: number
  // AI analysis
  ai_score?: number
  ai_recommendation?: 'approved' | 'rejected' | 'uncertain'
  ai_reason?: string
  analyzing?: boolean
}

// * Upload result for single domain
interface UploadResult {
  domain: string
  success: boolean
  is_duplicate?: boolean
  error?: string
  vdl_id?: number
}

// * AI Config from CRM
interface AIConfig {
  niche?: string
  description?: string
  crm_script?: string
  vdl_tz?: string
  project_name?: string
  regions?: string[]
  queries?: string[]
  ai_instructions?: string
  focus_keywords?: string[]
  business_keywords?: string[]
  exclude_keywords?: string[]
  target_audiences?: string[]
}

interface ParsingWorkflowModalProps {
  visible: boolean
  taskId: string | null | undefined
  projectId: number | null | undefined
  projectCrmId: number | null | undefined
  projectName: string | null | undefined
  initialDomains: any[]
  parsingResult: any
  onClose: () => void
  onComplete: () => void
}

export default function ParsingWorkflowModal({
  visible,
  taskId,
  projectId,
  projectCrmId,
  projectName,
  initialDomains,
  parsingResult,
  onClose,
  onComplete,
}: ParsingWorkflowModalProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [domains, setDomains] = useState<DomainWithAnalysis[]>([])
  const [aiConfig, setAIConfig] = useState<AIConfig | null>(null)
  const [loadingAIConfig, setLoadingAIConfig] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [aiAnalysisSkipped, setAiAnalysisSkipped] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([])
  const [searchText, setSearchText] = useState('')
  
  // * AI Analysis filters
  const [aiFilter, setAiFilter] = useState<'all' | 'approved' | 'rejected' | 'uncertain'>('all')
  const [aiSearchText, setAiSearchText] = useState('')
  const [lastSaved, setLastSaved] = useState<string | null>(null)
  const [showAllDomains, setShowAllDomains] = useState(false)

  // * Manual save function - now uses backend API
  const saveWorkflowState = async () => {
    if (taskId && currentStep > 0) {
      try {
        const state = {
          currentStep,
          domains,
          aiConfig,
          aiAnalysisSkipped,
          analyzing,
          timestamp: new Date().toISOString()
        }
        await api.saveWorkflowState(taskId, state)
        setLastSaved(new Date().toLocaleTimeString())
        message.success('Состояние workflow сохранено на сервере', 2)
      } catch (error) {
        console.error('Failed to save workflow state:', error)
        message.error('Ошибка сохранения состояния workflow')
      }
    }
  }

  // * Restore workflow state from backend
  useEffect(() => {
    const restoreWorkflowState = async () => {
      if (visible && taskId && projectId) {
        try {
          const response = await api.getWorkflowState(taskId)
          if (response.state) {
            const state = response.state
            if (state.currentStep !== undefined) {
              setCurrentStep(state.currentStep)
            }
            if (state.domains) {
              // ? Reset 'analyzing' flag on all domains to prevent stuck state
              const restoredDomains = state.domains.map((d: DomainWithAnalysis) => ({
                ...d,
                analyzing: false
              }))
              setDomains(restoredDomains)
            }
            if (state.aiConfig) {
              setAIConfig(state.aiConfig)
            }
            if (state.aiAnalysisSkipped !== undefined) {
              setAiAnalysisSkipped(state.aiAnalysisSkipped)
            }
            
            // ? Check if analysis was interrupted
            const wasAnalyzing = state.analyzing === true
            const hasPartialResults = state.domains?.some((d: DomainWithAnalysis) => d.ai_score !== undefined)
            const hasIncompleteResults = state.domains?.some((d: DomainWithAnalysis) => d.selected && d.ai_score === undefined)
            
            if (wasAnalyzing && hasIncompleteResults) {
              message.warning({
                content: 'AI анализ был прерван. Вы можете продолжить анализ оставшихся доменов.',
                duration: 5
              })
            } else if (hasPartialResults) {
              message.info('Восстановлено состояние workflow с результатами AI анализа с сервера')
            } else {
              message.info('Восстановлено состояние workflow с сервера')
            }
          }
        } catch (error) {
          // * No saved state or error - это нормально для первого запуска
          console.log('No saved workflow state found (this is normal for first run)')
        }
      }
    }
    
    restoreWorkflowState()
  }, [visible, taskId, projectId])

  // * Auto-save workflow state to backend on changes
  // ! CRITICAL: Save state frequently during AI analysis to prevent data loss on refresh
  useEffect(() => {
    const autoSave = async () => {
      if (visible && taskId && currentStep > 0) {
        try {
          const state = {
            currentStep,
            domains,
            aiConfig,
            aiAnalysisSkipped,
            analyzing,
            timestamp: new Date().toISOString()
          }
          await api.saveWorkflowState(taskId, state)
        } catch (error) {
          // * Silent failure for auto-save - не беспокоим пользователя
          console.warn('Auto-save failed:', error)
        }
      }
    }
    
    // * Debounce auto-save to avoid too many requests
    const timer = setTimeout(autoSave, 1000)
    return () => clearTimeout(timer)
  }, [currentStep, domains, aiConfig, aiAnalysisSkipped, analyzing, visible, taskId])

  // * Initialize domains
  useEffect(() => {
    if (visible && initialDomains && initialDomains.length > 0) {
      const formattedDomains: DomainWithAnalysis[] = initialDomains.map((d) => ({
        ...d,
        selected: true, // All selected by default
      }))
      setDomains(formattedDomains)
    }
  }, [visible, initialDomains])

  // * Load AI Config from CRM
  useEffect(() => {
    if (visible && projectId && currentStep === 1) {
      loadAIConfig()
    }
  }, [visible, projectId, currentStep])

  const loadAIConfig = async () => {
    if (!projectId) return

    try {
      setLoadingAIConfig(true)
      
      // * Load project AI config (includes crm_script, vdl_tz from CRM)
      const config = await api.getProjectAIConfig(projectId)
      
      // * Load queries and regions from VDL (not from parsing result!)
      const queriesRegions = await api.getProjectQueriesRegions(projectId)
      
      // * Enrich with VDL data (original queries/regions, not parsed results)
      const enrichedConfig: AIConfig = {
        ...config,
        project_name: projectName || undefined,
        queries: queriesRegions.queries || [],
        regions: queriesRegions.regions?.map((r: any) => r.name || r.id) || [],
      }
      
      setAIConfig(enrichedConfig)
      message.success('AI конфигурация загружена из CRM и VDL')
    } catch (error) {
      console.error('Failed to load AI config:', error)
      message.warning('Не удалось загрузить AI конфигурацию. Будет использована базовая.')
      
      // * Fallback config
      setAIConfig({
        project_name: projectName || undefined,
        queries: parsingResult?.queries?.map((q: any) => q.query) || [],
        regions: parsingResult?.regions || [],
      })
    } finally {
      setLoadingAIConfig(false)
    }
  }

  const handleAnalyze = async () => {
    if (!projectId) {
      message.error('Проект не выбран')
      return
    }

    try {
      setAnalyzing(true)
      message.info('🚀 Запуск параллельного AI анализа...')

      // * Analyze selected domains with CONCURRENCY LIMIT
      // ? Only analyze domains that don't have results yet
      const selectedDomains = domains.filter((d) => d.selected && d.ai_score === undefined)
      
      if (selectedDomains.length === 0) {
        message.info('Все выбранные домены уже проанализированы')
        setAnalyzing(false)
        return
      }
      
      const analyzedDomains = [...domains]

      // * Mark selected unanalyzed domains as analyzing
      selectedDomains.forEach((domain) => {
        const index = analyzedDomains.findIndex((d) => d.host === domain.host)
        if (index !== -1) {
          analyzedDomains[index].analyzing = true
        }
      })
      setDomains([...analyzedDomains])

      // * Parallel processing with concurrency limit (max 20 simultaneous requests)
      const MAX_CONCURRENT = 20
      let completed = 0

      const analyzeSingleDomain = async (domain: typeof selectedDomains[0]) => {
        const index = analyzedDomains.findIndex((d) => d.host === domain.host)
        if (index === -1) return { success: false, domain: domain.host }

        try {
          // * Call AI analyzer
          const result = await api.analyzeDomain({
            url: `https://${domain.host}`,
            ai_config_id: projectCrmId || projectId,
          })

          analyzedDomains[index] = {
            ...analyzedDomains[index],
            ai_score: result.score,
            ai_recommendation: result.recommendation,
            ai_reason: result.reason || result.analysis?.summary,
            analyzing: false,
          }

          // * Auto-deselect rejected domains
          if (result.recommendation === 'rejected') {
            analyzedDomains[index].selected = false
          }

          // * Update UI for this completed domain
          completed++
          setDomains([...analyzedDomains])
          
          // ? Save state to backend after each domain completion for crash recovery
          if (taskId && completed % 5 === 0) {
            try {
              const progressState = {
                currentStep,
                domains: analyzedDomains,
                aiConfig,
                aiAnalysisSkipped,
                analyzing: true,
                timestamp: new Date().toISOString()
              }
              // * Fire and forget - не блокируем анализ
              api.saveWorkflowState(taskId, progressState).catch(console.warn)
            } catch (error) {
              // * Ignore save errors during analysis
            }
          }
          
          return { success: true, domain: domain.host, result }
        } catch (error) {
          console.error(`Error analyzing ${domain.host}:`, error)
          analyzedDomains[index] = {
            ...analyzedDomains[index],
            ai_recommendation: 'uncertain',
            ai_reason: 'Ошибка анализа',
            analyzing: false,
          }
          
          // * Update UI for this failed domain
          completed++
          setDomains([...analyzedDomains])
          
          return { success: false, domain: domain.host, error }
        }
      }

      // * Process domains in batches with concurrency control
      const results = []
      for (let i = 0; i < selectedDomains.length; i += MAX_CONCURRENT) {
        const batch = selectedDomains.slice(i, i + MAX_CONCURRENT)
        const batchResults = await Promise.allSettled(
          batch.map(domain => analyzeSingleDomain(domain))
        )
        results.push(...batchResults)
        
        // * Show progress
        if (i + MAX_CONCURRENT < selectedDomains.length) {
          message.info(`📊 Обработано ${completed}/${selectedDomains.length} доменов...`)
        }
      }

      const approved = analyzedDomains.filter((d) => d.ai_recommendation === 'approved').length
      const rejected = analyzedDomains.filter((d) => d.ai_recommendation === 'rejected').length

      message.success(
        `✅ AI анализ завершен: ${approved} одобрено, ${rejected} отклонено`
      )
      
      // * Save final state to backend immediately after analysis completes
      if (taskId) {
        try {
          const finalState = {
            currentStep,
            domains: analyzedDomains,
            aiConfig,
            aiAnalysisSkipped,
            analyzing: false,
            timestamp: new Date().toISOString()
          }
          await api.saveWorkflowState(taskId, finalState)
        } catch (error) {
          console.warn('Failed to save final state:', error)
        }
      }
    } catch (error: any) {
      message.error('Ошибка AI анализа: ' + (error.message || 'Неизвестная ошибка'))
    } finally {
      setAnalyzing(false)
    }
  }

  const handleUpload = async () => {
    if (!projectCrmId) {
      message.error('CRM ID проекта не найден')
      return
    }

    const selectedDomains = domains.filter((d) => d.selected)

    if (selectedDomains.length === 0) {
      message.warning('Не выбрано ни одного домена для загрузки')
      return
    }

    try {
      setUploading(true)
      
      // ? Save state to backend before upload
      if (taskId) {
        try {
          const uploadState = {
            currentStep: 3,
            domains,
            aiConfig,
            aiAnalysisSkipped,
            analyzing: false,
            timestamp: new Date().toISOString()
          }
          await api.saveWorkflowState(taskId, uploadState)
        } catch (error) {
          console.warn('Failed to save state before upload:', error)
        }
      }
      
      message.info(`Загрузка ${selectedDomains.length} доменов в VDL...`)

      const results: UploadResult[] = []

      // * Batch upload
      const response = await api.uploadDomainsToVDL({
        crm_id: projectCrmId,
        domains: selectedDomains.map(d => d.host),
        subsource: `yandex_parsing_${taskId || 'manual'}`
      })

      // * Parse results
      if (response.details && Array.isArray(response.details)) {
        response.details.forEach((detail: any, index: number) => {
          results.push({
            domain: selectedDomains[index].host,
            success: detail.success,
            is_duplicate: detail.is_duplicate || false,
            error: detail.error || detail.message,
            vdl_id: detail.source_id,
          })
        })
      } else {
        // * Fallback if no detailed results
        selectedDomains.forEach((d) => {
          results.push({
            domain: d.host,
            success: true, // Assume success if no details
            is_duplicate: false,
          })
        })
      }

      setUploadResults(results)

      const newlyAddedCount = results.filter((r) => r.success && !r.is_duplicate).length
      const duplicateCount = results.filter((r) => r.success && r.is_duplicate).length
      const failCount = results.filter((r) => !r.success).length

      if (failCount === 0 && duplicateCount === 0) {
        message.success(`Все ${newlyAddedCount} доменов успешно загружены в VDL!`)
      } else if (failCount === 0 && duplicateCount > 0) {
        message.info(
          `Загружено: ${newlyAddedCount} новых. Дубликаты: ${duplicateCount}.`
        )
      } else {
        message.warning(
          `Загружено: ${newlyAddedCount} новых. Дубликаты: ${duplicateCount}. Ошибки: ${failCount}.`
        )
      }

      // * Move to results step
      setCurrentStep(4)
      
      // ? Clear workflow state from backend after successful upload
      if (taskId) {
        try {
          await api.clearWorkflowState(taskId)
        } catch (error) {
          console.warn('Failed to clear workflow state:', error)
        }
      }
    } catch (error: any) {
      message.error('Ошибка загрузки в VDL: ' + (error.message || 'Неизвестная ошибка'))
    } finally {
      setUploading(false)
    }
  }

  const handleNext = () => {
    if (currentStep === 1) {
      // * Before moving to step 2 (manual editing), check if AI analysis is needed
      const selectedDomains = domains.filter((d) => d.selected)
      const unanalyzedSelected = selectedDomains.filter((d) => !d.ai_score)
      
      if (!analyzing && unanalyzedSelected.length > 0 && !aiAnalysisSkipped) {
        message.warning({
          content: `${unanalyzedSelected.length} выбранных доменов не проанализированы. Запустите AI анализ или пропустите его.`,
          duration: 5
        })
        return
      }
    }
    
    if (currentStep === 2) {
      // * Skip old "preview" step, go straight to confirm & upload
      setCurrentStep(currentStep + 1)
    } else {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrev = () => {
    if (currentStep === 4) {
      // * Go back from results to editing (skip confirm)
      setCurrentStep(2)
    } else {
      setCurrentStep(currentStep - 1)
    }
  }

  const toggleDomainSelection = (host: string) => {
    setDomains(
      domains.map((d) => (d.host === host ? { ...d, selected: !d.selected } : d))
    )
  }

  const toggleAllSelection = (checked: boolean) => {
    setDomains(domains.map((d) => ({ ...d, selected: checked })))
  }

  const removeDomain = (host: string) => {
    setDomains(domains.filter((d) => d.host !== host))
    message.success(`Домен ${host} удален`)
  }

  // * Filtered domains for search
  const filteredDomains = searchText
    ? domains.filter((d) => d.host.toLowerCase().includes(searchText.toLowerCase()))
    : domains

  const selectedCount = domains.filter((d) => d.selected).length

  // * Step 0: Parsing Results
  const renderParsingResults = () => {
    const isBatch = (parsingResult?.total_combinations || 0) > 1

    const columns: ColumnsType<any> = [
      {
        title: '#',
        key: 'index',
        width: 50,
        render: (_: any, __: any, index: number) => index + 1,
      },
      {
        title: 'Домен',
        dataIndex: 'host',
        key: 'host',
        ellipsis: true,
        sorter: (a, b) => a.host.localeCompare(b.host),
        render: (host: string) => (
          <a href={`https://${host}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
            {host}
          </a>
        ),
      },
      {
        title: 'Клики',
        dataIndex: 'total_clicks',
        key: 'total_clicks',
        width: 120,
        sorter: (a, b) => a.total_clicks - b.total_clicks,
        render: (value: number) => (
          <Text strong style={{ color: '#1890ff' }}>
            {value.toLocaleString()}
          </Text>
        ),
      },
      {
        title: 'Запросов',
        dataIndex: 'queries_count',
        key: 'queries_count',
        width: 100,
        sorter: (a, b) => a.queries_count - b.queries_count,
      },
      {
        title: 'Позиция',
        dataIndex: 'average_position',
        key: 'average_position',
        width: 100,
        sorter: (a, b) => a.average_position - b.average_position,
        render: (value: number) => value.toFixed(1),
      },
    ]

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Success Header */}
        <Card
          bordered={false}
          style={{
            background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
            color: 'white',
          }}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <CheckCircleOutlined style={{ fontSize: 32 }} />
              <div>
                <Title level={4} style={{ margin: 0, color: 'white' }}>
                  Парсинг завершен!
                </Title>
                <Text style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  {isBatch
                    ? `Обработано ${parsingResult?.total_combinations || 0} комбинаций за ${parsingResult?.duration_seconds || 0}с`
                    : `Выполнено за ${parsingResult?.duration_seconds || 0} секунд`}
                </Text>
              </div>
            </div>
          </Space>
        </Card>

        {/* Stats Cards */}
        <Row gutter={16}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Доменов найдено"
                value={parsingResult?.domains_count || 0}
                valueStyle={{ color: '#1890ff' }}
                prefix={<RocketOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="URL обработано"
                value={parsingResult?.urls_count || 0}
                valueStyle={{ color: '#52c41a' }}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Поисковых запросов"
                value={parsingResult?.queries_count || 0}
                valueStyle={{ color: '#722ed1' }}
                prefix={<ExperimentOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Domains Table */}
        <Card title={<span>🏆 Найденные домены</span>} size="small">
          <Table
            dataSource={domains}
            columns={columns}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Всего: ${total}`,
            }}
            size="small"
            rowKey="host"
          />
        </Card>

        <Alert
          message="Следующий шаг: AI анализ"
          description="Система автоматически проанализирует домены по критериям из CRM: ниша, ТЗ, регионы и запросы."
          type="info"
          showIcon
          icon={<ExperimentOutlined />}
        />
      </Space>
    )
  }

  // * Step 1: AI Analysis
  const renderAIAnalysis = () => {
    const analyzedCount = domains.filter((d) => d.ai_score !== undefined).length
    const progressPercent = domains.length > 0 ? (analyzedCount / domains.length) * 100 : 0
    
    // * Calculate statistics
    const approvedCount = domains.filter((d) => d.ai_recommendation === 'approved').length
    const rejectedCount = domains.filter((d) => d.ai_recommendation === 'rejected').length
    const uncertainCount = domains.filter((d) => d.ai_recommendation === 'uncertain').length
    
    // * Filter and search logic
    const filteredDomains = domains.filter((d) => {
      // ? Filter by AI recommendation
      if (aiFilter !== 'all' && d.ai_recommendation !== aiFilter) return false
      
      // ? Search filter
      if (aiSearchText && !d.host.toLowerCase().includes(aiSearchText.toLowerCase())) return false
      
      return d.ai_score !== undefined
    })
    
    // * Table columns
    const aiAnalysisColumns: ColumnsType<DomainWithAnalysis> = [
      {
        title: 'Домен',
        dataIndex: 'host',
        key: 'host',
        width: '30%',
        ellipsis: true,
        render: (host: string) => (
          <a href={`https://${host}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff', fontWeight: 600, fontSize: 13 }}>
            {host}
          </a>
        ),
      },
      {
        title: 'Статус',
        key: 'recommendation',
        width: 120,
        align: 'center' as const,
        render: (_: any, record: DomainWithAnalysis) => {
          if (!record.ai_recommendation) return <Tag color="default">Не проанализирован</Tag>
          
          const config = {
            approved: { icon: <CheckCircleOutlined />, color: 'success', text: 'Одобрен' },
            rejected: { icon: <CloseCircleOutlined />, color: 'error', text: 'Отклонен' },
            uncertain: { icon: <QuestionCircleOutlined />, color: 'warning', text: 'Требует проверки' },
          }[record.ai_recommendation]
          
          return (
            <Tag color={config.color} icon={config.icon}>
              {config.text}
            </Tag>
          )
        },
      },
      {
        title: 'Оценка AI',
        key: 'score',
        width: 100,
        align: 'center' as const,
        sorter: (a, b) => (a.ai_score || 0) - (b.ai_score || 0),
        render: (_: any, record: DomainWithAnalysis) => {
          if (!record.ai_score) return <Text type="secondary">—</Text>
          
          const getColor = () => {
            if (record.ai_score! >= 70) return '#52c41a'
            if (record.ai_score! >= 40) return '#faad14'
            return '#ff4d4f'
          }
          
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Progress
                type="circle"
                percent={record.ai_score}
                size={48}
                strokeColor={getColor()}
                format={(percent) => <span style={{ fontSize: 12, fontWeight: 600 }}>{percent}</span>}
              />
            </div>
          )
        },
      },
      {
        title: 'Причина',
        key: 'reason',
        ellipsis: true,
        render: (_: any, record: DomainWithAnalysis) => {
          const reason = record.ai_reason || 'Причина не указана'
          const truncated = reason.length > 60 ? `${reason.substring(0, 60)}...` : reason
          
          if (reason.length > 60) {
            return (
              <Popover
                content={
                  <div style={{ maxWidth: 400 }}>
                    <Text>{reason}</Text>
                  </div>
                }
                title="Полная причина"
                trigger="hover"
              >
                <Text type="secondary" style={{ cursor: 'help' }}>
                  {truncated} <InfoCircleOutlined style={{ fontSize: 12, marginLeft: 4 }} />
                </Text>
              </Popover>
            )
          }
          
          return <Text type="secondary">{reason}</Text>
        },
      },
      {
        title: 'Клики',
        dataIndex: 'total_clicks',
        key: 'clicks',
        width: 100,
        align: 'right' as const,
        sorter: (a, b) => a.total_clicks - b.total_clicks,
        render: (clicks: number) => (
          <Badge
            count={clicks.toLocaleString()}
            showZero
            style={{ backgroundColor: '#1890ff' }}
          />
        ),
      },
    ]

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card
          title={
            <Space>
              <ExperimentOutlined style={{ color: '#722ed1', fontSize: 20 }} />
              <span style={{ fontSize: 16, fontWeight: 600 }}>AI Анализ доменов</span>
            </Space>
          }
          extra={
            aiConfig && (
              <Tooltip title="Конфигурация AI">
                <Popover
                  content={
                    <Space direction="vertical" size="small" style={{ maxWidth: 600, maxHeight: 500, overflowY: 'auto' }}>
                    {aiConfig.project_name && (
                      <div>
                          <Text type="secondary">Проект: </Text>
                          <Text strong>{aiConfig.project_name}</Text>
                      </div>
                    )}
                    {aiConfig.niche && (
                      <div>
                          <Text type="secondary">Ниша: </Text>
                          <Tag color="blue">{aiConfig.niche}</Tag>
                      </div>
                    )}
                      {aiConfig.queries && aiConfig.queries.length > 0 && (
                      <div>
                          <Text type="secondary">Запросов: </Text>
                          <Text>{aiConfig.queries.length}</Text>
                      </div>
                    )}
                      {aiConfig.regions && aiConfig.regions.length > 0 && (
                      <div>
                          <Text type="secondary">Регионов: </Text>
                          <Text>{aiConfig.regions.length}</Text>
                      </div>
                    )}
                      {aiConfig.ai_instructions && (
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" strong style={{ display: 'block', marginBottom: 4 }}>
                            Промпт к нейронке:
                          </Text>
                          <div style={{ 
                            background: '#f5f5f5', 
                            padding: '8px 12px', 
                            borderRadius: 4,
                            fontSize: 12,
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                            maxHeight: 300,
                            overflowY: 'auto'
                          }}>
                            {aiConfig.ai_instructions}
                          </div>
                        </div>
                      )}
                      {aiConfig.focus_keywords && aiConfig.focus_keywords.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" strong style={{ display: 'block', marginBottom: 4 }}>
                            Фокус-слова:
                          </Text>
                          <Space wrap size={4}>
                            {aiConfig.focus_keywords.slice(0, 10).map((kw: string, idx: number) => (
                              <Tag key={idx} color="green" style={{ fontSize: 11 }}>{kw}</Tag>
                            ))}
                            {aiConfig.focus_keywords.length > 10 && (
                              <Tag style={{ fontSize: 11 }}>+{aiConfig.focus_keywords.length - 10}</Tag>
                            )}
                          </Space>
                        </div>
                      )}
                      {aiConfig.business_keywords && aiConfig.business_keywords.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" strong style={{ display: 'block', marginBottom: 4 }}>
                            Бизнес-ключи:
                          </Text>
                          <Space wrap size={4}>
                            {aiConfig.business_keywords.slice(0, 10).map((kw: string, idx: number) => (
                              <Tag key={idx} color="blue" style={{ fontSize: 11 }}>{kw}</Tag>
                            ))}
                            {aiConfig.business_keywords.length > 10 && (
                              <Tag style={{ fontSize: 11 }}>+{aiConfig.business_keywords.length - 10}</Tag>
                            )}
                          </Space>
                        </div>
                      )}
                      {aiConfig.exclude_keywords && aiConfig.exclude_keywords.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" strong style={{ display: 'block', marginBottom: 4 }}>
                            Слова исключения:
                          </Text>
                          <Space wrap size={4}>
                            {aiConfig.exclude_keywords.slice(0, 10).map((kw: string, idx: number) => (
                              <Tag key={idx} color="red" style={{ fontSize: 11 }}>{kw}</Tag>
                            ))}
                            {aiConfig.exclude_keywords.length > 10 && (
                              <Tag style={{ fontSize: 11 }}>+{aiConfig.exclude_keywords.length - 10}</Tag>
                            )}
                          </Space>
                        </div>
                      )}
                    </Space>
                  }
                  title="Конфигурация AI из CRM"
                  trigger="click"
                >
                  <Button size="small" icon={<InfoCircleOutlined />}>
                    Конфигурация
                  </Button>
                </Popover>
                              </Tooltip>
            )
          }
        >
          {loadingAIConfig ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Загрузка AI конфигурации из CRM...</Text>
                        </div>
                      </div>
          ) : (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* * Analysis progress */}
              {analyzing && (
                <Alert
                  message={
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <span>Анализ доменов: {analyzedCount} из {domains.length}</span>
                      <Text strong>{Math.round(progressPercent)}%</Text>
                  </Space>
                  }
                  description={<Progress percent={Math.round(progressPercent)} status="active" showInfo={false} />}
                  type="info"
                  icon={<LoadingOutlined />}
                />
              )}

              {/* * Start analysis */}
              {!analyzing && analyzedCount === 0 && (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <ExperimentOutlined style={{ fontSize: 64, color: '#722ed1', marginBottom: 16 }} />
                  <Title level={4}>Готово к AI анализу</Title>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                    Искусственный интеллект проанализирует {domains.length} {domains.length === 1 ? 'домен' : 'доменов'} по критериям из CRM
                  </Text>
                  <Button type="primary" icon={<ExperimentOutlined />} onClick={handleAnalyze} size="large">
                    Запустить AI анализ
                  </Button>
                </div>
              )}
              
              {/* * Resume analysis after page refresh */}
              {!analyzing && analyzedCount > 0 && analyzedCount < domains.filter(d => d.selected).length && (
                <Alert
                  message="Анализ не завершен"
                  description={
                    <Space direction="vertical" size="small">
                      <Text>
                        Проанализировано {analyzedCount} из {domains.filter(d => d.selected).length} выбранных доменов.
                      </Text>
                      <Button 
                        type="primary" 
                        icon={<ExperimentOutlined />} 
                        onClick={handleAnalyze}
                        size="small"
                      >
                        Продолжить анализ оставшихся доменов
                      </Button>
                    </Space>
                  }
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {/* * Analysis results */}
              {analyzedCount > 0 && (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {/* ? Statistics cards */}
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={6}>
                      <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                      <Statistic
                          title={<Text style={{ fontSize: 12 }}>Одобрено</Text>}
                          value={approvedCount}
                          valueStyle={{ color: '#52c41a', fontSize: 28, fontWeight: 700 }}
                          prefix={<CheckCircleOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                      <Card size="small" style={{ background: '#fff1f0', borderColor: '#ffccc7' }}>
                      <Statistic
                          title={<Text style={{ fontSize: 12 }}>Отклонено</Text>}
                          value={rejectedCount}
                          valueStyle={{ color: '#ff4d4f', fontSize: 28, fontWeight: 700 }}
                          prefix={<CloseCircleOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                      <Card size="small" style={{ background: '#fffbe6', borderColor: '#ffe58f' }}>
                      <Statistic
                          title={<Text style={{ fontSize: 12 }}>Требует проверки</Text>}
                          value={uncertainCount}
                          valueStyle={{ color: '#faad14', fontSize: 28, fontWeight: 700 }}
                          prefix={<QuestionCircleOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                      <Card size="small" style={{ background: '#fafafa', borderColor: '#d9d9d9' }}>
                        <Statistic
                          title={<Text style={{ fontSize: 12 }}>Всего проанализировано</Text>}
                          value={analyzedCount}
                          valueStyle={{ fontSize: 28, fontWeight: 700 }}
                          suffix={<Text type="secondary" style={{ fontSize: 16 }}>/ {domains.length}</Text>}
                        />
                      </Card>
                    </Col>
                  </Row>

                  {/* ? Filters and search */}
                  <Card size="small">
                    <Row gutter={16} align="middle">
                      <Col flex="auto">
                        <Space>
                          <FilterOutlined style={{ color: '#8c8c8c' }} />
                          <Segmented
                            options={[
                              { label: `Все (${analyzedCount})`, value: 'all' },
                              { 
                                label: (
                                  <Space size={4}>
                                    <span>Одобрено</span>
                                    <Badge count={approvedCount} style={{ backgroundColor: '#52c41a' }} />
                                  </Space>
                                ), 
                                value: 'approved' 
                              },
                              { 
                                label: (
                                  <Space size={4}>
                                    <span>Отклонено</span>
                                    <Badge count={rejectedCount} style={{ backgroundColor: '#ff4d4f' }} />
                                  </Space>
                                ), 
                                value: 'rejected' 
                              },
                              { 
                                label: (
                                  <Space size={4}>
                                    <span>Проверить</span>
                                    <Badge count={uncertainCount} style={{ backgroundColor: '#faad14' }} />
                                  </Space>
                                ), 
                                value: 'uncertain' 
                              },
                            ]}
                            value={aiFilter}
                            onChange={(value) => setAiFilter(value as any)}
                          />
                        </Space>
                      </Col>
                      <Col flex="300px">
                        <Input
                          placeholder="Поиск по доменам..."
                          prefix={<SearchOutlined />}
                          allowClear
                          value={aiSearchText}
                          onChange={(e) => setAiSearchText(e.target.value)}
                      />
                    </Col>
                  </Row>
                </Card>

                  {/* ? Domains table */}
                  <Card size="small" bodyStyle={{ padding: 0 }}>
                    <Table
                      columns={aiAnalysisColumns}
                      dataSource={filteredDomains}
                      rowKey="host"
                      pagination={{
                        defaultPageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total) => `Всего: ${total} доменов`,
                        size: 'small',
                      }}
                      size="small"
                      locale={{
                        emptyText: (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                              aiFilter !== 'all' || aiSearchText
                                ? 'Нет доменов, соответствующих фильтрам'
                                : 'Нет проанализированных доменов'
                            }
                          />
                        ),
                      }}
                    />
                  </Card>
                </Space>
              )}
            </Space>
          )}
        </Card>
      </Space>
    )
  }

  // * Step 2: Manual Editing
  const renderManualEditing = () => {
    const columns: ColumnsType<DomainWithAnalysis> = [
      {
        title: (
          <Checkbox
            checked={selectedCount === domains.length && domains.length > 0}
            indeterminate={selectedCount > 0 && selectedCount < domains.length}
            onChange={(e) => toggleAllSelection(e.target.checked)}
          />
        ),
        key: 'checkbox',
        width: 50,
        render: (_: any, record: DomainWithAnalysis) => (
          <Checkbox
            checked={record.selected}
            onChange={() => toggleDomainSelection(record.host)}
          />
        ),
      },
      {
        title: 'URL',
        dataIndex: 'full_url',
        key: 'full_url',
        ellipsis: true,
        filteredValue: searchText ? [searchText] : null,
        onFilter: (value, record) => {
          const fullUrl = record.full_url || `https://${record.host}`
          return fullUrl.toLowerCase().includes((value as string).toLowerCase())
        },
        render: (_: any, record: DomainWithAnalysis) => {
          const url = record.full_url || `https://${record.host}`
          const displayUrl = url.replace(/^https?:\/\//, '') // Remove protocol for display

          return (
            <Space direction="vertical" size={0}>
              <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
                {displayUrl}
              </a>
              {record.path && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {record.domain} • {record.path}
                </Text>
              )}
            </Space>
          )
        },
      },
      {
        title: 'AI Оценка',
        key: 'ai_result',
        width: 150,
        render: (_: any, record: DomainWithAnalysis) => {
          if (!record.ai_score) {
            return <Tag color="default">Не анализировался</Tag>
          }

          const getColor = () => {
            if (record.ai_recommendation === 'approved') return 'success'
            if (record.ai_recommendation === 'rejected') return 'error'
            return 'warning'
          }

          const getIcon = () => {
            if (record.ai_recommendation === 'approved')
              return <CheckCircleOutlined />
            if (record.ai_recommendation === 'rejected') return <CloseCircleOutlined />
            return <WarningOutlined />
          }

          return (
            <Tooltip title={record.ai_reason}>
              <Space size="small">
                <Tag color={getColor()} icon={getIcon()}>
                  {record.ai_score}/100
                </Tag>
              </Space>
            </Tooltip>
          )
        },
      },
      {
        title: 'Клики',
        dataIndex: 'total_clicks',
        key: 'total_clicks',
        width: 100,
        sorter: (a, b) => a.total_clicks - b.total_clicks,
        render: (value: number) => value.toLocaleString(),
      },
      {
        title: 'Запросов',
        dataIndex: 'queries_count',
        key: 'queries_count',
        width: 100,
        sorter: (a, b) => a.queries_count - b.queries_count,
      },
      {
        title: 'Телефоны',
        key: 'phones',
        width: 120,
        render: (_: any, record: any) => {
          const phonesCount = record.phones_extracted_count || 0
          const extractionStatus = record.phone_extraction_status || 'not_started'

          if (extractionStatus === 'extracting') {
            return (
              <Tag icon={<LoadingOutlined />} color="processing">
                Извлекаются...
              </Tag>
            )
          }

          if (extractionStatus === 'failed') {
            return (
              <Tooltip title={record.phone_extraction_error || 'Ошибка извлечения'}>
                <Tag icon={<CloseCircleOutlined />} color="error">
                  Ошибка
                </Tag>
              </Tooltip>
            )
          }

          if (phonesCount > 0) {
            return (
              <Tag color="success" style={{ cursor: 'pointer' }}>
                <Space size={4}>
                  <CheckCircleOutlined />
                  {phonesCount} тел.
                </Space>
              </Tag>
            )
          }

          return <Tag color="default">Нет телефонов</Tag>
        },
      },
      {
        title: 'Действия',
        key: 'actions',
        width: 80,
        render: (_: any, record: DomainWithAnalysis) => (
          <Popconfirm
            title="Удалить домен?"
            description={`Вы уверены, что хотите удалить ${record.host}?`}
            onConfirm={() => removeDomain(record.host)}
            okText="Да"
            cancelText="Нет"
          >
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        ),
      },
    ]

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card
          title={
            <Space>
              <EditOutlined style={{ color: '#1890ff' }} />
              <span>Ручное редактирование списка</span>
              <Badge count={selectedCount} style={{ backgroundColor: '#52c41a' }} />
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* Search */}
            <Search
              placeholder="Поиск по URL..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
            />

            {/* Quick Actions */}
            <Space wrap>
              <Button
                size="small"
                onClick={() => toggleAllSelection(true)}
              >
                Выбрать все
              </Button>
              <Button
                size="small"
                onClick={() => toggleAllSelection(false)}
              >
                Снять все
              </Button>
              <Divider type="vertical" />
              <Button
                size="small"
                type="primary"
                ghost
                onClick={() => {
                  // Select only approved
                  setDomains(
                    domains.map((d) => ({
                      ...d,
                      selected: d.ai_recommendation === 'approved',
                    }))
                  )
                }}
              >
                Только одобренные AI
              </Button>
              <Button
                size="small"
                danger
                ghost
                onClick={() => {
                  // Deselect rejected
                  setDomains(
                    domains.map((d) => ({
                      ...d,
                      selected: d.selected && d.ai_recommendation !== 'rejected',
                    }))
                  )
                }}
              >
                Убрать отклоненные AI
              </Button>
            </Space>

            {/* Domains Table */}
            <Table
              dataSource={filteredDomains}
              columns={columns}
              pagination={{
                defaultPageSize: 15,
                showSizeChanger: true,
                pageSizeOptions: ['15', '30', '50', '100'],
                showTotal: (total) => `Всего: ${total} | Выбрано: ${selectedCount}`,
              }}
              size="small"
              rowKey="host"
              rowClassName={(record) => (!record.selected ? 'row-disabled' : '')}
            />

            <Alert
              message={`Выбрано ${selectedCount} URL для загрузки в VDL`}
              type={selectedCount > 0 ? 'success' : 'warning'}
              showIcon
            />
          </Space>
        </Card>
      </Space>
    )
  }

  // * Step 3: Confirm & Upload (combined preview + upload)
  const renderConfirmUpload = () => {
    const selectedDomains = domains.filter((d) => d.selected)

    if (selectedDomains.length === 0) {
      return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            message="Не выбрано ни одного домена"
            description="Вернитесь на предыдущий шаг и выберите домены для загрузки"
            type="warning"
            showIcon
          />
        </Space>
      )
    }

    const approvedByAi = selectedDomains.filter((d) => d.ai_recommendation === 'approved')
    const rejectedByAi = selectedDomains.filter((d) => d.ai_recommendation === 'rejected')
    const uncertainByAi = selectedDomains.filter((d) => d.ai_recommendation === 'uncertain')
    const noAiRecommendation = selectedDomains.filter((d) => !d.ai_recommendation)

    const displayLimit = 30
    const domainsToShow = showAllDomains ? selectedDomains : selectedDomains.slice(0, displayLimit)
    const hasMoreDomains = selectedDomains.length > displayLimit

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card
          title={
            <Space>
              <CloudUploadOutlined style={{ color: '#1890ff' }} />
              <span>Подтверждение загрузки</span>
            </Space>
          }
        >
          {uploading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">
                  Загрузка {selectedDomains.length} доменов в VDL...
                </Text>
              </div>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Это может занять некоторое время</Text>
              </div>
            </div>
          ) : (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Project Info */}
              <Card size="small" type="inner">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Проект:</Text> <Text>{projectName}</Text>
                  </div>
                  <div>
                    <Text strong>CRM ID:</Text> <Tag color="blue">{projectCrmId}</Tag>
                  </div>
                  <div>
                    <Text strong>Доменов:</Text> <Tag color="green" style={{ fontSize: 14 }}>{selectedDomains.length}</Tag>
                  </div>
                  <div>
                    <Text strong>Subsource:</Text>{' '}
                    <Tag>yandex_parsing_{taskId || 'manual'}</Tag>
                  </div>
                </Space>
              </Card>

              {/* Statistics */}
              <Card size="small">
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic 
                      title="Всего к загрузке" 
                      value={selectedDomains.length} 
                      prefix={<CloudUploadOutlined />} 
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Одобрено AI"
                      value={approvedByAi.length}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Отклонено AI"
                      value={rejectedByAi.length}
                      prefix={<CloseCircleOutlined />}
                      valueStyle={{ color: '#ff4d4f' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Неопределённо"
                      value={uncertainByAi.length + noAiRecommendation.length}
                      valueStyle={{ color: '#faad14' }}
                    />
                  </Col>
                </Row>
              </Card>

              {/* Domains List */}
              <Card 
                size="small" 
                title={
                  <Space>
                    <span>Список доменов для загрузки</span>
                    {hasMoreDomains && (
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => setShowAllDomains(!showAllDomains)}
                      >
                        {showAllDomains ? 'Свернуть' : `Показать все (${selectedDomains.length})`}
                      </Button>
                    )}
                  </Space>
                }
                style={{ maxHeight: 400, overflow: 'auto' }}
              >
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {domainsToShow.map((domain, index) => {
                    const getRecommendationColor = () => {
                      if (!domain.ai_recommendation) return 'default'
                      switch (domain.ai_recommendation) {
                        case 'approved': return 'success'
                        case 'rejected': return 'error'
                        case 'uncertain': return 'warning'
                        default: return 'default'
                      }
                    }

                    const getRecommendationIcon = () => {
                      if (!domain.ai_recommendation) return null
                      switch (domain.ai_recommendation) {
                        case 'approved': return <CheckCircleOutlined />
                        case 'rejected': return <CloseCircleOutlined />
                        default: return null
                      }
                    }

                    return (
                      <div 
                        key={domain.host}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          background: index % 2 === 0 ? '#fafafa' : '#fff',
                          borderRadius: 4,
                        }}
                      >
                        <Space>
                          <Text type="secondary" style={{ minWidth: 35 }}>{index + 1}.</Text>
                          <Text strong>{domain.host}</Text>
                        </Space>
                        <Space size="small">
                          {domain.ai_score !== undefined && domain.ai_score !== null && (
                            <Tooltip title="AI Score">
                              <Badge 
                                count={domain.ai_score.toFixed(2)} 
                                style={{ 
                                  backgroundColor: domain.ai_score > 0.7 ? '#52c41a' : domain.ai_score > 0.4 ? '#faad14' : '#ff4d4f',
                                  fontSize: 11,
                                  padding: '0 6px'
                                }}
                              />
                            </Tooltip>
                          )}
                          {domain.ai_recommendation && (
                            <Tag 
                              icon={getRecommendationIcon()}
                              color={getRecommendationColor()}
                              style={{ margin: 0, fontSize: 11 }}
                            >
                              {domain.ai_recommendation === 'approved' ? 'Одобрен' : 
                               domain.ai_recommendation === 'rejected' ? 'Отклонен' : 
                               'Неопределённо'}
                            </Tag>
                          )}
                        </Space>
                      </div>
                    )
                  })}
                  {!showAllDomains && hasMoreDomains && (
                    <div style={{ textAlign: 'center', padding: '12px', background: '#f5f5f5', borderRadius: 4 }}>
                      <Text type="secondary">
                        ... и ещё {selectedDomains.length - displayLimit} доменов
                      </Text>
                    </div>
                  )}
                </Space>
              </Card>

              <Alert
                message="Внимание"
                description="После загрузки домены будут добавлены с статусом 'Не работает' (will_work=False) и доступны для проверки в Swipe Mode."
                type="info"
                showIcon
              />

              <Button
                type="primary"
                size="large"
                icon={<CloudUploadOutlined />}
                onClick={handleUpload}
                block
              >
                Загрузить в VDL ({selectedDomains.length} доменов)
              </Button>
            </Space>
          )}
        </Card>
      </Space>
    )
  }

  // * Step 4: Upload Results
  const renderUploadResults = () => {
    const newlyAddedCount = uploadResults.filter((r) => r.success && !r.is_duplicate).length
    const duplicateCount = uploadResults.filter((r) => r.success && r.is_duplicate).length
    const successCount = newlyAddedCount + duplicateCount
    const failCount = uploadResults.filter((r) => !r.success).length
    const successRate = uploadResults.length > 0
      ? ((successCount / uploadResults.length) * 100).toFixed(1)
      : '0'

    const columns: ColumnsType<UploadResult> = [
      {
        title: '#',
        key: 'index',
        width: 50,
        render: (_: any, __: any, index: number) => index + 1,
      },
      {
        title: 'Домен',
        dataIndex: 'domain',
        key: 'domain',
        ellipsis: true,
        render: (domain: string) => (
          <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
            {domain}
          </a>
        ),
      },
      {
        title: 'Статус',
        dataIndex: 'success',
        key: 'success',
        width: 130,
        render: (success: boolean, record: UploadResult) => {
          if (!success) {
            return (
              <Tag icon={<CloseCircleOutlined />} color="error">
                Ошибка
              </Tag>
            )
          }
          if (record.is_duplicate) {
            return (
              <Tag icon={<WarningOutlined />} color="warning">
                Дубликат
              </Tag>
            )
          }
          return (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Загружен
            </Tag>
          )
        },
        filters: [
          { text: 'Загружен', value: 'uploaded' },
          { text: 'Дубликат', value: 'duplicate' },
          { text: 'Ошибка', value: 'error' },
        ],
        onFilter: (value, record) => {
          if (value === 'uploaded') return record.success && !(record.is_duplicate ?? false)
          if (value === 'duplicate') return record.success && (record.is_duplicate ?? false)
          if (value === 'error') return !record.success
          return false
        },
      },
      {
        title: 'VDL ID',
        dataIndex: 'vdl_id',
        key: 'vdl_id',
        width: 100,
        render: (id?: number) => (id ? <Tag color="blue">{id}</Tag> : '-'),
      },
      {
        title: 'Ошибка',
        dataIndex: 'error',
        key: 'error',
        ellipsis: true,
        render: (error?: string) => (error ? <Text type="danger">{error}</Text> : '-'),
      },
    ]

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Result
          status={failCount === 0 ? 'success' : 'warning'}
          title={
            failCount === 0
              ? 'Все домены успешно загружены!'
              : 'Загрузка завершена с ошибками'
          }
          subTitle={`Загружено: ${newlyAddedCount} | Дубликаты: ${duplicateCount} | Ошибки: ${failCount} | Процент успеха: ${successRate}%`}
          extra={[
            <Button type="primary" key="close" onClick={onComplete}>
              Закрыть и вернуться к проекту
            </Button>,
          ]}
        />

        {/* Stats */}
        <Row gutter={16}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Успешно загружено"
                value={newlyAddedCount}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Дубликаты"
                value={duplicateCount}
                valueStyle={{ color: '#fa8c16' }}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Ошибок загрузки"
                value={failCount}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Results Table */}
        <Card title="Детальные результаты" size="small">
          <Table
            dataSource={uploadResults}
            columns={columns}
            pagination={{
              defaultPageSize: 15,
              showSizeChanger: true,
              pageSizeOptions: ['15', '30', '50', '100'],
              showTotal: (total) => `Всего: ${total}`,
            }}
            size="small"
            rowKey="domain"
            rowClassName={(record) => (record.success ? '' : 'row-error')}
          />
        </Card>
      </Space>
    )
  }

  // * Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderParsingResults()
      case 1:
        return renderAIAnalysis()
      case 2:
        return renderManualEditing()
      case 3:
        return renderConfirmUpload() // Combined preview + upload
      case 4:
        return renderUploadResults()
      default:
        return null
    }
  }

  // * Navigation buttons
  const renderNavigationButtons = () => {
    if (currentStep === 4) {
      // Results step - no navigation needed
      return null
    }

    if (currentStep === 3) {
      // Confirm & Upload step - only back button (upload in card)
      return (
        <Space>
          <Button onClick={handlePrev}>Назад</Button>
        </Space>
      )
    }

    const canProceed = currentStep === 1 ? (aiAnalysisSkipped || analyzedCount === domains.length) : true

    return (
      <Space>
        {currentStep > 0 && (
          <Button onClick={handlePrev}>Назад</Button>
        )}
        
        <Button
          type="primary"
          onClick={handleNext}
          disabled={!canProceed}
        >
          Далее
        </Button>
      </Space>
    )
  }

  const analyzedCount = domains.filter((d) => d.ai_score !== undefined).length

  const steps = [
    {
      title: 'Результаты парсинга',
      icon: <RocketOutlined />,
      description: `${domains.length} доменов`,
    },
    {
      title: 'AI Анализ',
      icon: <ExperimentOutlined />,
      description: analyzing
        ? `${analyzedCount}/${domains.length}`
        : aiAnalysisSkipped
        ? 'Пропущен'
        : analyzedCount > 0
        ? 'Завершен'
        : 'Не запущен',
    },
    {
      title: 'Редактирование',
      icon: <EditOutlined />,
      description: `Выбрано: ${selectedCount}`,
    },
    {
      title: 'Загрузка в VDL',
      icon: <CloudUploadOutlined />,
      description: uploading ? 'Загрузка...' : uploadResults.length > 0 ? 'Завершено' : 'Готово',
    },
    {
      title: 'Результаты',
      icon: <CheckCircleOutlined />,
      description: uploadResults.length > 0 ? 'Завершено' : 'Ожидание',
    },
  ]

  return (
    <Modal
      title={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <RocketOutlined />
            <span>Обработка результатов парсинга</span>
          </Space>
          <Space size="small">
            {lastSaved && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Сохранено: {lastSaved}
              </Text>
            )}
            {currentStep > 0 && currentStep < 4 && (
              <Tooltip title="Сохранить текущее состояние workflow">
                <Button 
                  size="small" 
                  icon={<SaveOutlined />}
                  onClick={saveWorkflowState}
                >
                  Сохранить
                </Button>
              </Tooltip>
            )}
          </Space>
        </Space>
      }
      open={visible}
      onCancel={currentStep === 5 ? onComplete : onClose}
      footer={null}
      width={1000}
      destroyOnClose
      maskClosable={false}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Steps */}
        <Steps current={currentStep} size="small">
          {steps.map((step, index) => (
            <Step
              key={index}
              title={step.title}
              description={step.description}
              icon={step.icon}
            />
          ))}
        </Steps>

        <Divider style={{ margin: '12px 0' }} />

        {/* Step Content */}
        <div style={{ minHeight: 400 }}>{renderStepContent()}</div>

        {/* Navigation */}
        <div style={{ textAlign: 'right' }}>{renderNavigationButtons()}</div>
      </Space>

      <style>
        {`
          .row-disabled {
            opacity: 0.5;
            background-color: #f5f5f5;
          }
          .row-error {
            background-color: #fff1f0;
          }
        `}
      </style>
    </Modal>
  )
}

